import { useCallback, useEffect, useRef } from 'react'

/**
 * Creates a callback that executes after a specified delay. Each call creates
 * a new timeout. All timeouts are cleared on unmount.
 *
 * @param callback - Function to call
 * @param delay - Delay in milliseconds
 * @returns A function that schedules the callback with given args
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDelayedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const schedule = useCallback(
    (...args: Parameters<T>) => {
      const timeout = setTimeout(() => {
        callback(...args)
      }, delay)

      timeoutsRef.current.push(timeout)
    },
    [callback, delay],
  )

  useEffect(() => {
    return () => {
      // Clear all pending timeouts on unmount
      timeoutsRef.current.forEach(clearTimeout)
      timeoutsRef.current = []
    }
  }, [])

  return schedule
}
